import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Divider,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import semver from "semver";

import { selectLastSeenReleaseVersion, setToken } from "@/auth/authSlice";
import { request } from "@/Api";
import { APP_NAME, APP_VERSION } from "@/config";

export default function ReleaseNotesDialog() {
    const dispatch = useDispatch();
    const lastSeenReleaseVersion = useSelector(selectLastSeenReleaseVersion);

    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!APP_VERSION || !lastSeenReleaseVersion) {
            return;
        }

        try {
            if (
                semver.valid(APP_VERSION) &&
                semver.valid(lastSeenReleaseVersion) &&
                semver.gt(APP_VERSION, lastSeenReleaseVersion)
            ) {
                setOpen(true);
                fetchNotes();
            }
        } catch (e) {
            console.error("Version comparison failed:", e);
        }
    }, [lastSeenReleaseVersion]);

    const fetchNotes = async () => {
        setLoading(true);
        try {
            const response = await fetch("/release-notes.json");
            const data = await response.json();

            let newNotes = data.filter((note) => {
                if (!semver.valid(note.version)) {
                    return false;
                }
                return (
                    semver.gt(note.version, lastSeenReleaseVersion) &&
                    semver.lte(note.version, APP_VERSION)
                );
            });
            newNotes.sort((a, b) => semver.rcompare(a.version, b.version));

            setNotes(newNotes);
        } catch (error) {
            console.error("Failed to fetch release notes:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = async () => {
        try {
            await request.post("/account/last-seen-release", {
                version: APP_VERSION,
            });

            request.post(`refresh-token`, {}).then((response) => {
                dispatch(setToken(response.data));
            });
        } catch (error) {
            console.error("Failed to update last seen release version:", error);
        } finally {
            setOpen(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, paddingBottom: "0" }}>
                {APP_NAME} Updates
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box
                        sx={{ display: "flex", justifyContent: "center", p: 4 }}
                    >
                        <CircularProgress />
                    </Box>
                ) : notes.length > 0 ? (
                    notes.map((note, index) => (
                        <Box
                            key={note.version}
                            sx={{ mb: index < notes.length - 1 ? 4 : 0 }}
                        >
                            <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                            >
                                {note.title}{" "}
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (v{note.version})
                                </Typography>
                            </Typography>
                            <Box
                                sx={{
                                    "& img": {
                                        maxWidth: "100%",
                                        height: "auto",
                                    },
                                    "& a": { color: "primary.main" },
                                }}
                            >
                                <ReactMarkdown>{note.content}</ReactMarkdown>
                            </Box>
                            {index < notes.length - 1 && (
                                <Divider sx={{ mt: 3 }} />
                            )}
                        </Box>
                    ))
                ) : (
                    <Typography color="textSecondary">
                        Welcome to version {APP_VERSION ? APP_VERSION : ""}.
                        There are no release notes available for this update.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleDismiss}
                    variant="contained"
                    color="primary"
                    disableElevation
                >
                    Got it
                </Button>
            </DialogActions>
        </Dialog>
    );
}
